import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { CommonStyle } from "Components";
import { DropdownArrPropsType } from "DataTypes/Form";
import { CPTeamSearchType1 } from "DataTypes/Services/Careplan.type";
import { NotificationType } from "DataTypes/Notification";
import { FC, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setAlertData, setLeftHead, setNavArr, setRightHead } from "Redux/Screen/Action";
import { UserInitialStateType, ReduxScreenNavType } from "DataTypes/Redux";
import { RouteUrls } from "Constant/RouteUrls";
import CarePlanApi from "Service/CarePlan.api";
import Notification from "Service/Notification";
import UserApi from "Service/User.api";
import CPFormatter from "Utils/CP.Formatter";
import { FormatDropdownData } from "Utils/DataFormatter";
import NotificationFormatter from "Utils/Notification.Formatter";
import UsersFormatter from "Utils/Users.Formatter";
import CommonFormatter from "Utils/Common.Formatter";
import { CheckArr, CheckNumber, CheckStringEmpty } from "Utils/common";
import { store } from "Redux/Store";

const ROLE_RENDERING = "RENDERING";
const ROLE_BILLABLE = "BILLABLE";
const ROLE_SUPERVISING = "SUPERVISING";

type ProviderScreenMode = "transition" | "replacement";

type ProviderTransitionState = {
  notificationId?: number | null;
  careplanId?: number | null;
  disabledProviderId?: number | null;
  providerId?: number | null;
  mode?: ProviderScreenMode;
};

type DisabledProviderOption = {
  id: number;
  label: string;
};

type ProviderTransitionItem = {
  id: number | null;
  disabledProviderId: number | null;
  patientId: string | null;
  name: string;
  careplanId: number | null;
  message: string;
  practiceName: string;
  datetime: string;
  rolesToReplace: string[];
  careplanState: string;
  programAbbreviation: string;
  updatedBy: string;
  updatedAt: string;
  reassigned: boolean;
  reassignmentStatus: string;
  reassignedTo: string;
  reassignedBy: string;
  reassignedAt: string;
};

type StatusFilter = "pending" | "reassigned";

const ProviderTransitionWorklist: FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { myInfo }: UserInitialStateType = useSelector(
    (state: any) => state?.user
  );
  const routeState = (location.state || {}) as ProviderTransitionState;
  const isReplacementMode =
    routeState.mode === "replacement" ||
    (!!routeState.providerId && !routeState.disabledProviderId);
  const isTransitionMode = !isReplacementMode;
  const canBulkReplace = myInfo.roleId == 1 || myInfo.roleId == 2;
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [notifications, setNotifications] = useState<
    ProviderTransitionItem[]
  >([]);
  const [selectedOldProviderId, setSelectedOldProviderId] =
    useState<number | null>(
      routeState.disabledProviderId || routeState.providerId || null
    );
  const [team, setTeam] = useState<CPTeamSearchType1 | null>(null);
  const [providers, setProviders] = useState<DropdownArrPropsType[]>([]);
  const [newProviderId, setNewProviderId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [providerLoading, setProviderLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedCareplanIds, setSelectedCareplanIds] = useState<number[]>([]);
  const [careplanStateMap, setCareplanStateMap] = useState<
    Record<number, string>
  >({});
  const [providerOrganization, setProviderOrganization] = useState("");
  const [fallbackProviderOption, setFallbackProviderOption] =
    useState<DisabledProviderOption | null>(null);

  useEffect(() => {
    dispatch(setLeftHead(""));
    dispatch(setRightHead(""));
    const { navArr } = store.getState().screen;
    const withoutPatientNav = navArr.filter(
      (el: ReduxScreenNavType) => el.navType !== "patient"
    );
    const hasOrgNav = withoutPatientNav.some(
      (el: ReduxScreenNavType) => el.navType === "org"
    );
    const navType: ReduxScreenNavType["navType"] = hasOrgNav ? "org" : "my-items";
    const siblingNav = withoutPatientNav.filter(
      (el: ReduxScreenNavType) => el.navType === navType
    );
    const level =
      siblingNav.length > 0
        ? Math.max(...siblingNav.map((el: ReduxScreenNavType) => el.level)) + 1
        : 1;
    dispatch(setNavArr(withoutPatientNav));
    CommonFormatter.HandleNavArr({
      id: isReplacementMode ? "provider-replacement" : "provider-transition",
      label: isReplacementMode ? "Provider Replacement" : "Provider Transition",
      path: `/${RouteUrls.hs}/${RouteUrls.providerTransition}`,
      level,
      link: RouteUrls.providerTransition,
      navType,
    });
  }, [dispatch, isReplacementMode]);

  useEffect(() => {
    initialLoad();
  }, []);

  const allProviderOptions = useMemo(() => {
    const map = new Map<number, DisabledProviderOption>();
    notifications.forEach((notification) => {
      const providerId = getOldProviderId(notification);
      if (!providerId || map.has(providerId)) return;
      const fallbackLabel =
        fallbackProviderOption?.id === providerId
          ? fallbackProviderOption.label
          : "";
      map.set(providerId, {
        id: providerId,
        label: fallbackLabel || getDisabledProviderName(notification) || "Provider",
      });
    });
    if (fallbackProviderOption && !map.has(fallbackProviderOption.id)) {
      map.set(fallbackProviderOption.id, fallbackProviderOption);
    }
    return Array.from(map.values());
  }, [notifications, fallbackProviderOption]);

  const selectedProviderOption =
    allProviderOptions.find((option) => option.id === selectedOldProviderId) ||
    null;

  const eligibleAffectedAlerts = useMemo(() => {
    if (!selectedOldProviderId) return [];
    return notifications.filter(
      (notification) =>
        notification.disabledProviderId === selectedOldProviderId &&
        !!notification.careplanId
    );
  }, [notifications, selectedOldProviderId]);

  const selectedAlerts = useMemo(
    () =>
      eligibleAffectedAlerts.filter(
        (alert) =>
          !!alert.careplanId && selectedCareplanIds.includes(alert.careplanId)
      ),
    [eligibleAffectedAlerts, selectedCareplanIds]
  );

  const anchorAlert = useMemo(() => {
    return eligibleAffectedAlerts.find(
      (notification) => notification.careplanId === routeState.careplanId
    ) || eligibleAffectedAlerts[0] || null;
  }, [
    eligibleAffectedAlerts,
    routeState.careplanId,
  ]);

  const actionableAffectedAlerts = useMemo(
    () =>
      eligibleAffectedAlerts.filter(
        (alert) =>
          !!alert.careplanId &&
          !alert.reassigned &&
          getRolesFromAlert(alert).length > 0
      ),
    [eligibleAffectedAlerts]
  );

  const filteredAffectedAlerts = useMemo(() => {
    if (statusFilter === "pending") {
      return eligibleAffectedAlerts.filter((alert) => !alert.reassigned);
    }
    if (statusFilter === "reassigned") {
      return eligibleAffectedAlerts.filter((alert) => alert.reassigned);
    }
    return eligibleAffectedAlerts;
  }, [eligibleAffectedAlerts, statusFilter]);

  const pendingCount = useMemo(
    () => eligibleAffectedAlerts.filter((alert) => !alert.reassigned).length,
    [eligibleAffectedAlerts]
  );

  const reassignedCount = useMemo(
    () => eligibleAffectedAlerts.filter((alert) => alert.reassigned).length,
    [eligibleAffectedAlerts]
  );

  const isTransitionAssignmentComplete =
    isTransitionMode &&
    eligibleAffectedAlerts.length > 0 &&
    pendingCount === 0;

  const displayAffectedAlerts = useMemo(() => {
    if (isReplacementMode) {
      return eligibleAffectedAlerts;
    }
    return filteredAffectedAlerts;
  }, [eligibleAffectedAlerts, filteredAffectedAlerts, isReplacementMode]);

  const isPendingTransitionTab =
    isTransitionMode && statusFilter === "pending";

  const showBulkReplaceActions =
    canBulkReplace &&
    (isReplacementMode || (isTransitionMode && statusFilter === "pending"));

  const showCareplanSignedOffColumn = isTransitionMode;

  const showPendingTransitionStatusColumn = isPendingTransitionTab;

  const showTransitionDetailColumns =
    isTransitionMode && statusFilter === "reassigned";

  const tableColumnCount =
    (showBulkReplaceActions ? 1 : 0) +
    3 +
    (showCareplanSignedOffColumn ? 1 : 0) +
    (showPendingTransitionStatusColumn ? 1 : 0) +
    (isReplacementMode ? 2 : 0) +
    (showTransitionDetailColumns ? 4 : 0);

  const selectedProvider =
    providers.find((provider) => provider.value === newProviderId) || null;

  const roleCounts = useMemo(() => {
    return eligibleAffectedAlerts.reduce(
      (acc, alert) => {
        const roles = getRolesFromAlert(alert);
        if (roles.includes(ROLE_RENDERING)) acc.rendering += 1;
        if (roles.includes(ROLE_BILLABLE)) acc.carePlanRendering += 1;
        if (roles.includes(ROLE_SUPERVISING)) acc.supervising += 1;
        if (
          roles.includes(ROLE_RENDERING) &&
          roles.includes(ROLE_BILLABLE) &&
          roles.includes(ROLE_SUPERVISING)
        ) {
          acc.allThree += 1;
        }
        return acc;
      },
      { rendering: 0, carePlanRendering: 0, supervising: 0, allThree: 0 }
    );
  }, [eligibleAffectedAlerts]);

  useEffect(() => {
    if (allProviderOptions.length && !selectedOldProviderId) {
      setSelectedOldProviderId(allProviderOptions[0].id);
    }
  }, [allProviderOptions, selectedOldProviderId]);

  useEffect(() => {
    setSelectedCareplanIds([]);
    setProviderOrganization("");
    if (selectedOldProviderId) {
      loadSelectedProviderContext(selectedOldProviderId);
    }
  }, [selectedOldProviderId]);

  useEffect(() => {
    const eligibleCareplanIds = actionableAffectedAlerts
      .map((alert) => alert.careplanId)
      .filter((id): id is number => !!id);
    setSelectedCareplanIds((prev) =>
      prev.filter((id) => eligibleCareplanIds.includes(id))
    );
  }, [actionableAffectedAlerts]);

  useEffect(() => {
    setNewProviderId(null);
    setProviders([]);
    setTeam(null);
    if (anchorAlert?.careplanId) loadReplacementProviders(anchorAlert);
  }, [anchorAlert?.careplanId]);

  const initialLoad: () => Promise<void> = async () => {
    setLoading(true);
    if (isReplacementMode && selectedOldProviderId) {
      await loadFallbackDisabledProviderOption(selectedOldProviderId);
      await loadEligibleCareplans(selectedOldProviderId);
      setLoading(false);
      return;
    }
    const res = await Notification.search({
      data: {
        receiverUserId: myInfo.id,
      },
      limit: 500,
      offset: 0,
    });
    if (res?.success) {
      const list = NotificationFormatter.Arr(res.data)
        .filter(
          (notification) =>
            notification.action === "PROVIDER_REASSIGNMENT_REQUIRED"
        )
        .map(toProviderTransitionItem);
      const disabledProviderNotifications =
        await filterNotificationsForCurrentlyDisabledProviders(list);
      setNotifications(disabledProviderNotifications);
      if (selectedOldProviderId) {
        if (
          !disabledProviderNotifications.some(
            (notification) =>
              getOldProviderId(notification) === selectedOldProviderId
          )
        ) {
          await loadFallbackDisabledProviderOption(selectedOldProviderId);
        }
        if (
          isReplacementMode ||
          await isProviderCurrentlyDisabled(selectedOldProviderId)
        ) {
          await loadEligibleCareplans(selectedOldProviderId);
        } else {
          clearSelectedProviderRows(selectedOldProviderId);
        }
      }
    } else {
      if (selectedOldProviderId) {
        await loadFallbackDisabledProviderOption(selectedOldProviderId);
        if (
          isReplacementMode ||
          await isProviderCurrentlyDisabled(selectedOldProviderId)
        ) {
          await loadEligibleCareplans(selectedOldProviderId);
        } else {
          clearSelectedProviderRows(selectedOldProviderId);
        }
      } else {
        setNotifications([]);
      }
    }
    setLoading(false);
  };

  const loadFallbackDisabledProviderOption: (providerId: number) => Promise<void> =
    async (providerId) => {
      const userRes = await UserApi.read({ id1: providerId });
      if (!userRes?.success) return;
      const user = UsersFormatter.VerifyReadResponse(userRes.data);
      setFallbackProviderOption({
        id: providerId,
        label: user.fullNameDes || "Provider",
      });
      if (!providerOrganization) {
        setProviderOrganization(user.practice?.[0]?.name || "");
      }
    };

  const loadSelectedProviderContext: (providerId: number) => Promise<void> =
    async (providerId) => {
      await loadFallbackDisabledProviderOption(providerId);
      if (
        !isReplacementMode &&
        !(await isProviderCurrentlyDisabled(providerId))
      ) {
        clearSelectedProviderRows(providerId);
        return;
      }
      await loadEligibleCareplans(providerId);
    };

  const clearSelectedProviderRows: (providerId: number) => void = (
    providerId
  ) => {
    setNotifications((prev) =>
      prev.filter(
        (notification) =>
          notification.disabledProviderId !== providerId ||
          notification.careplanId == null
      )
    );
    setCareplanStateMap({});
    setSelectedCareplanIds([]);
    setProviderOrganization("");
  };

  const loadEligibleCareplans: (providerId: number) => Promise<void> = async (
    providerId
  ) => {
    setLoading(true);
    const res = await CarePlanApi.ProviderReassignmentEligibleCareplanDetails({
      id1: providerId,
      data: { includeHistory: !isReplacementMode },
    });
    if (res?.success) {
      const notificationDate =
        notifications.find(
          (notification) =>
            notification.disabledProviderId === providerId &&
            notification.careplanId == null
        )?.datetime || "";
      const rows = CheckArr(res.data).map((row) =>
        toProviderTransitionCareplanItem(row, providerId, notificationDate)
      );
      setNotifications((prev) => [
        ...prev.filter((notification) => notification.careplanId == null),
        ...rows,
      ]);
      setProviderOrganization(rows[0]?.practiceName || "");
      const nextStateMap: Record<number, string> = {};
      rows.forEach((row) => {
        if (row.careplanId) {
          nextStateMap[row.careplanId] = row.careplanState;
        }
      });
      setCareplanStateMap(nextStateMap);
    }
    setLoading(false);
  };

  const filterNotificationsForCurrentlyDisabledProviders = async (
    providerNotifications: ProviderTransitionItem[]
  ): Promise<ProviderTransitionItem[]> => {
    const statusByProviderId = new Map<number, boolean>();
    const disabledNotifications: ProviderTransitionItem[] = [];

    for (const notification of providerNotifications) {
      const providerId = getOldProviderId(notification);
      if (!providerId) continue;
      if (!statusByProviderId.has(providerId)) {
        const userRes = await UserApi.read({ id1: providerId });
        const user = userRes?.success
          ? UsersFormatter.VerifyReadResponse(userRes.data)
          : null;
        statusByProviderId.set(
          providerId,
          isDisabledProviderStatus(user?.status)
        );
      }
      if (statusByProviderId.get(providerId)) {
        disabledNotifications.push(notification);
      }
    }

    return disabledNotifications;
  };

  const isProviderCurrentlyDisabled: (providerId: number) => Promise<boolean> =
    async (providerId) => {
      const userRes = await UserApi.read({ id1: providerId });
      const user = userRes?.success
        ? UsersFormatter.VerifyReadResponse(userRes.data)
        : null;
      return isDisabledProviderStatus(user?.status);
    };

  const loadReplacementProviders: (
    notification: ProviderTransitionItem
  ) => Promise<void> =
    async (notification) => {
      if (!notification.careplanId) return;
      setProviderLoading(true);
      const teamRes = await CarePlanApi.CPTeamSearch({
        id1: notification.careplanId,
      });
      if (teamRes?.success) {
        const checkedTeam = CPFormatter.CheckCPTeam1(teamRes.data);
        setTeam(checkedTeam);
        const providerRes = await UserApi.list({
          data: {
            practiceLocationId: checkedTeam.practiceLocationId,
            roleId: 5,
          },
        });
        if (providerRes?.success) {
          const oldProviderId = getOldProviderId(notification);
          const providerArr = UsersFormatter.VerifyListArrResponse(
            providerRes.data
          ).filter(
            (el) =>
              el.id !== oldProviderId &&
              (el.status === "ACTIVE" || el.status === "ENABLED")
          );
          setProviders(
            FormatDropdownData(providerArr, "id", "fullNameDes", "id")
          );
        }
      }
      setProviderLoading(false);
    };

  const handleSubmit: () => Promise<void> = async () => {
    if (
      !selectedAlerts[0]?.careplanId ||
      !selectedOldProviderId ||
      !newProviderId ||
      !selectedCareplanIds.length
    ) {
      return;
    }
    const submitAlert = selectedAlerts[0];
    setSaving(true);
    const res = await CarePlanApi.ProviderReassignment({
      id1: submitAlert.careplanId,
      data: {
        oldProviderId: selectedOldProviderId,
        newProviderId,
        rolesToReplace: getRequestedRoles(
          submitAlert,
          team,
          selectedOldProviderId
        ),
        reason: "",
        alertId: null,
        selectedCareplanIds,
        applyToAllAffected: true,
      },
    });
    if (res?.success) {
      dispatch(setAlertData(res.alert));
      await initialLoad();
    }
    setSaving(false);
  };

  const disableSubmit =
    loading ||
    providerLoading ||
    saving ||
    !anchorAlert ||
    !selectedOldProviderId ||
    !newProviderId ||
    !selectedCareplanIds.length ||
    !canBulkReplace;

  const allSelected =
    !!actionableAffectedAlerts.length &&
    actionableAffectedAlerts.every(
      (alert) =>
        !!alert.careplanId && selectedCareplanIds.includes(alert.careplanId)
    );

  const partiallySelected = selectedCareplanIds.length > 0 && !allSelected;

  const handleSelectAll: (checked: boolean) => void = (checked) => {
    setSelectedCareplanIds(
      checked
        ? actionableAffectedAlerts
          .map((alert) => alert.careplanId)
          .filter((id): id is number => !!id)
        : []
    );
  };

  const handleSelectRow: (
    careplanId: number | null,
    checked: boolean
  ) => void = (careplanId, checked) => {
    if (!careplanId) return;
    setSelectedCareplanIds((prev) =>
      checked
        ? Array.from(new Set([...prev, careplanId]))
        : prev.filter((id) => id !== careplanId)
    );
  };

  const handlePatientClick: (patientId: string | null) => void = (
    patientId
  ) => {
    if (!patientId) return;
    navigate(
      `/${RouteUrls.hs}/${RouteUrls.patient}/` +
      `${patientId}/${RouteUrls.clinicalSummary}`
    );
  };

  return (
    <Box height="100%" sx={{ overflow: "auto", py: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ ...CommonStyle.sxRow, justifyContent: "space-between" }}>
            <Box>
              <Box sx={{ ...CommonStyle.sxRow, gap: 1, alignItems: "center" }}>
                <Typography variant="h6">
                  {isReplacementMode
                    ? "Provider Replacement"
                    : "Provider Transition"}
                </Typography>
                {isTransitionMode && (
                  <Chip
                    size="small"
                    label={
                      isTransitionAssignmentComplete
                        ? "Assignment Completed"
                        : "Action Required"
                    }
                    color={
                      isTransitionAssignmentComplete ? "success" : "warning"
                    }
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="body2">
                {isReplacementMode
                  ? "Replace this provider across active enrolled patients. The provider is still active — this is a proactive change, not a disable transition."
                  : isTransitionAssignmentComplete
                    ? `All ${reassignedCount} affected patients have been reassigned to a new provider. This provider transition is complete.`
                    : "A provider has been disabled. Reassign their affected patients to a new provider."}
              </Typography>
              {providerOrganization && (
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, fontWeight: 600 }}
                >
                  Organization: {providerOrganization}
                </Typography>
              )}
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2">
            {isReplacementMode ? "Provider" : "Disabled Provider"}
          </Typography>
          <TextField
            fullWidth
            value={selectedProviderOption?.label || ""}
            placeholder={
              isReplacementMode ? "Select Provider" : "Select Disabled Provider"
            }
            InputProps={{ readOnly: true }}
          />
        </Grid>
        {showBulkReplaceActions && (
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">New Provider</Typography>
            <Autocomplete
              disablePortal
              clearIcon={false}
              loading={providerLoading}
              value={selectedProvider}
              options={providers}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(event, value) =>
                setNewProviderId(CheckNumber(value?.value))
              }
              renderInput={(params) => (
                <TextField {...params} placeholder="Select New Provider" />
              )}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Box sx={{ ...CommonStyle.sxRow, justifyContent: "space-between" }}>
            <Box>
              <Typography variant="subtitle2">
                {isReplacementMode ? "Active Patients" : "Affected Patients"}:{" "}
                {eligibleAffectedAlerts.length}
              </Typography>
              <Typography variant="body2">
                Rendering: {roleCounts.rendering} | Care Plan Rendering:{" "}
                {roleCounts.carePlanRendering} | Supervising:{" "}
                {roleCounts.supervising} | All Three: {roleCounts.allThree}
              </Typography>
              {isTransitionMode && (
                <Typography variant="body2">
                  Pending: {pendingCount} | Reassigned: {reassignedCount}
                </Typography>
              )}
              {showBulkReplaceActions && (
                <Typography variant="body2">
                  Selected Patients: {selectedCareplanIds.length}
                </Typography>
              )}
            </Box>
            {showBulkReplaceActions && (
              <Button
                variant="contained"
                disabled={disableSubmit}
                onClick={handleSubmit}
              >
                {saving ? "Replacing" : "Replace Selected"}
              </Button>
            )}
          </Box>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {showBulkReplaceActions
              ? isReplacementMode
                ? "Select patients and choose a new provider to replace this provider across active care teams."
                : "Select pending patients and choose a new provider to replace the disabled provider in bulk. Use a patient profile only for a single-patient provider change."
              : isTransitionMode && statusFilter === "reassigned" && canBulkReplace
                ? "Review patients that have been reassigned to a new provider."
                : canBulkReplace
                  ? isReplacementMode
                    ? "Select patients and choose a new provider to replace this provider across active care teams."
                    : "Select pending patients and choose a new provider to replace the disabled provider in bulk."
                  : "Open a patient profile to replace this provider for an individual patient."}
          </Typography>
          {isTransitionMode && (
            <Box sx={{ ...CommonStyle.sxRow, gap: 1, mt: 1 }}>
              <Button
                size="small"
                variant={statusFilter === "pending" ? "contained" : "outlined"}
                onClick={() => setStatusFilter("pending")}
              >
                Pending ({pendingCount})
              </Button>
              <Button
                size="small"
                variant={
                  statusFilter === "reassigned" ? "contained" : "outlined"
                }
                onClick={() => setStatusFilter("reassigned")}
              >
                Reassigned ({reassignedCount})
              </Button>
            </Box>
          )}
        </Grid>
        <Grid item xs={12}>
          {loading ? (
            <Box sx={{ ...CommonStyle.sxRowCenter, height: 180 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 430px)",
                minHeight: 220,
                overflow: "auto",
              }}
            >
              <Table stickyHeader size="small" sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        backgroundColor: "#fff",
                        position: "sticky",
                        top: 0,
                        zIndex: 3,
                      },
                    }}
                  >
                    {showBulkReplaceActions && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={allSelected}
                          indeterminate={partiallySelected}
                          disabled={!actionableAffectedAlerts.length}
                          onChange={(event) =>
                            handleSelectAll(event.target.checked)
                          }
                        />
                      </TableCell>
                    )}
                    <TableCell sx={{ width: 160 }}>Patient</TableCell>
                    <TableCell sx={{ width: 200 }}>Roles To Replace</TableCell>
                    {showCareplanSignedOffColumn && (
                      <TableCell sx={{ width: 120 }}>Careplan Signed Off</TableCell>
                    )}
                    {showPendingTransitionStatusColumn && (
                      <TableCell sx={{ width: 120 }}>Transition Status</TableCell>
                    )}
                    <TableCell sx={{ width: 88 }}>Program Enrolled</TableCell>
                    {isReplacementMode && (
                      <>
                        <TableCell sx={{ width: 160 }}>Updated By</TableCell>
                        <TableCell sx={{ width: 148 }}>Updated At</TableCell>
                      </>
                    )}
                    {showTransitionDetailColumns && (
                      <>
                        <TableCell sx={{ width: 120 }}>Transition Status</TableCell>
                        <TableCell sx={{ width: 180 }}>Reassigned To</TableCell>
                        <TableCell sx={{ width: 160 }}>Replaced By</TableCell>
                        <TableCell sx={{ width: 148 }}>Replaced At</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayAffectedAlerts.map((alert) => (
                    <TableRow
                      key={`${alert.id}-${alert.careplanId}`}
                      sx={
                        isTransitionMode && alert.reassigned
                          ? { backgroundColor: "rgba(46, 125, 50, 0.06)" }
                          : undefined
                      }
                    >
                      {showBulkReplaceActions && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              !!alert.careplanId &&
                              selectedCareplanIds.includes(alert.careplanId)
                            }
                            disabled={alert.reassigned}
                            onChange={(event) =>
                              handleSelectRow(
                                alert.careplanId,
                                event.target.checked
                              )
                            }
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ width: 160, verticalAlign: "top" }}>
                        <Button
                          variant="text"
                          disabled={!alert.patientId}
                          onClick={() => handlePatientClick(alert.patientId)}
                          sx={{
                            display: "block",
                            minWidth: 0,
                            maxWidth: 150,
                            p: 0,
                            color: "#002b5c",
                            fontWeight: 600,
                            lineHeight: 1.35,
                            textAlign: "left",
                            textDecoration: "underline",
                            textTransform: "none",
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                          }}
                        >
                          {alert.name}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          width: 200,
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          lineHeight: 1.35,
                        }}
                      >
                        {getRolesFromAlert(alert).map(getRoleLabel).join(", ") ||
                          "-"}
                      </TableCell>
                      {showCareplanSignedOffColumn && (
                        <TableCell sx={{ width: 120 }}>
                          <CareplanStatusChip
                            state={
                              alert.careplanId
                                ? careplanStateMap[alert.careplanId]
                                : ""
                            }
                          />
                        </TableCell>
                      )}
                      {showPendingTransitionStatusColumn && (
                        <TableCell sx={{ width: 120 }}>
                          <ReassignmentStatusChip
                            reassigned={false}
                            status="PENDING"
                          />
                        </TableCell>
                      )}
                      <TableCell sx={{ width: 88 }}>{alert.programAbbreviation || "-"}</TableCell>
                      {isReplacementMode && (
                        <>
                          <TableCell sx={{ width: 160 }}>{alert.updatedBy || "-"}</TableCell>
                          <TableCell sx={{ width: 148 }}>{alert.updatedAt || "-"}</TableCell>
                        </>
                      )}
                      {showTransitionDetailColumns && (
                        <>
                          <TableCell>
                            <ReassignmentStatusChip
                              reassigned={alert.reassigned}
                              status={alert.reassignmentStatus}
                            />
                          </TableCell>
                          <TableCell>
                            {formatReassignmentField(
                              alert.reassigned,
                              alert.reassignedTo
                            )}
                          </TableCell>
                          <TableCell>
                            {formatReassignmentField(
                              alert.reassigned,
                              alert.reassignedBy
                            )}
                          </TableCell>
                          <TableCell>
                            {formatReassignmentField(
                              alert.reassigned,
                              alert.reassignedAt
                            )}
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                  {!displayAffectedAlerts.length && (
                    <TableRow>
                      <TableCell colSpan={tableColumnCount}>
                        {isReplacementMode
                          ? "No active patients are currently assigned to this provider."
                          : "No provider reassignment notifications found for patients you are authorized to update."}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const toProviderTransitionItem = (
  notification: NotificationType
): ProviderTransitionItem => {
  return {
    id: notification.id,
    disabledProviderId: notification.actionId,
    patientId: notification.patientId,
    name: notification.patientName,
    careplanId: null,
    message: notification.message,
    practiceName: "",
    datetime: notification.datetime,
    rolesToReplace: [],
    careplanState: "",
    programAbbreviation: "",
    updatedBy: "",
    updatedAt: "",
    reassigned: false,
    reassignmentStatus: "PENDING",
    reassignedTo: "",
    reassignedBy: "",
    reassignedAt: "",
  };
};

const toProviderTransitionCareplanItem = (
  data: any,
  disabledProviderId: number,
  notificationDate: string
): ProviderTransitionItem => {
  return {
    id: null,
    disabledProviderId,
    patientId: CheckStringEmpty(data?.patientId),
    name: CheckStringEmpty(data?.patientName),
    careplanId: CheckNumber(data?.careplanId),
    message: "",
    practiceName: CheckStringEmpty(data?.practiceName),
    datetime: notificationDate,
    rolesToReplace: CheckArr(data?.rolesToReplace),
    careplanState: CheckStringEmpty(data?.careplanState),
    programAbbreviation: CheckStringEmpty(data?.programAbbreviation),
    updatedBy: getFirstString(
      data,
      "providerReassignmentUpdatedBy",
      "replacementUpdatedBy",
      "updatedByFullname",
      "updatedByFullName",
      "updatedBy",
      "modifiedBy",
      "addressedByUserName",
      "reassignedBy"
    ),
    updatedAt: getFirstString(
      data,
      "providerReassignmentUpdatedAt",
      "replacementUpdatedAt",
      "updatedAt",
      "updatedOn",
      "updatedDateTime",
      "updatedDate",
      "modifiedAt",
      "addressedAt",
      "reassignedAt"
    ),
    reassigned: Boolean(data?.reassigned),
    reassignmentStatus: CheckStringEmpty(data?.reassignmentStatus) ||
      (data?.reassigned ? "REASSIGNED" : "PENDING"),
    reassignedTo: data?.reassigned
      ? getFirstString(data, "reassignedTo", "newProviderName", "reassignedToFullname")
      : "",
    reassignedBy: data?.reassigned
      ? getFirstString(
        data,
        "reassignedBy",
        "updatedByFullname",
        "updatedByFullName",
        "providerReassignmentUpdatedBy",
        "replacementUpdatedBy"
      )
      : "",
    reassignedAt: data?.reassigned
      ? getFirstString(
        data,
        "reassignedAt",
        "updatedAt",
        "providerReassignmentUpdatedAt",
        "replacementUpdatedAt"
      )
      : "",
  };
};

const formatReassignmentField: (
  reassigned: boolean,
  value: string
) => string = (reassigned, value) => {
  if (!reassigned) return "-";
  return value || "-";
};

const getFirstString: (data: any, ...keys: string[]) => string = (
  data,
  ...keys
) => {
  for (const key of keys) {
    const value = CheckStringEmpty(data?.[key]);
    if (value) return value;
  }
  return "";
};

const getOldProviderId: (
  notification: ProviderTransitionItem | null
) => number | null = (
  notification
) => {
  if (notification?.disabledProviderId) return notification.disabledProviderId;
  const text = CheckStringEmpty(notification?.message);
  const match = text.match(/(?:disabledProviderId=|ID:\s*)(\d+)/i);
  return match ? Number(match[1]) : null;
};

const getDisabledProviderName: (
  notification: ProviderTransitionItem | null
) => string = (
  notification
) => {
  const message = CheckStringEmpty(notification?.message);
  const match = message.match(/^Provider\s+(.+?)\s+\(ID:/i);
  return match ? match[1] : "";
};

const getRolesFromAlert = (
  notification: ProviderTransitionItem | null
): string[] => {
  if (notification?.rolesToReplace?.length) {
    return notification.rolesToReplace;
  }
  const text = CheckStringEmpty(notification?.message);
  const roleMatch = text.match(/roles=([A-Z_,]+)/i);
  const roles = roleMatch
    ? roleMatch[1].split(",").map((role) => role.trim().toUpperCase())
    : [];
  return roles.filter(
    (role) =>
      role === ROLE_RENDERING ||
      role === ROLE_BILLABLE ||
      role === ROLE_SUPERVISING
  );
};

const getRequestedRoles = (
  alert: ProviderTransitionItem | null,
  team: CPTeamSearchType1 | null,
  oldProviderId: number | null
): string[] => {
  const roles = getRolesFromAlert(alert);
  if (roles.length) return roles;
  return [
    ...(team?.teamProviderId === oldProviderId ? [ROLE_RENDERING] : []),
    ...(team?.billableProviderId === oldProviderId ? [ROLE_BILLABLE] : []),
    ...(team?.supervisionProviderId === oldProviderId
      ? [ROLE_SUPERVISING]
      : []),
  ];
};

const getRoleLabel: (role: string) => string = (role) => {
  if (role === ROLE_RENDERING) return "Rendering Provider";
  if (role === ROLE_BILLABLE) return "Care Plan Rendering Provider";
  if (role === ROLE_SUPERVISING) return "Supervising Provider";
  return role;
};

const isSignedOffCareplanState: (state: string) => boolean = (state) => {
  return CheckStringEmpty(state).toLowerCase() === "pending for recurring";
};

const CareplanStatusChip: FC<{ state: string }> = ({ state }) => {
  const signedOff = isSignedOffCareplanState(state);
  return (
    <Chip
      size="small"
      label={state ? (signedOff ? "Yes" : "No") : "-"}
      sx={{
        minWidth: 72,
        color: signedOff ? "#008a3d" : "#d35400",
        backgroundColor: signedOff ? "#d9f4e4" : "#fff0df",
        fontWeight: 600,
      }}
    />
  );
};

const isDisabledProviderStatus: (
  status?: string | null
) => boolean = (status) => {
  const normalizedStatus = CheckStringEmpty(status).toUpperCase();
  return normalizedStatus === "DISABLED" || normalizedStatus === "INACTIVE";
};

const ReassignmentStatusChip: FC<{
  reassigned: boolean;
  status: string;
}> = ({ reassigned, status }) => {
  const label = reassigned || status === "REASSIGNED" ? "Reassigned" : "Pending";
  const color = label === "Reassigned" ? "success" : "warning";
  return <Chip size="small" label={label} color={color} variant="outlined" />;
};

export default ProviderTransitionWorklist;
